import { useContext, useRef, useState } from "react";
import { AuthService } from "../../application/services/authService";
import { AuthContext } from "../contexts/AuthContext";
import {
  clearAuthData,
  saveAuthData,
} from "../../infrastructure/storage/tokenStorage";

const authService = new AuthService();

export const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const {
    user,
    setUser,
    loading: authLoading,
    authChecked,
  } = useContext(AuthContext);

  let pendingRememberMe = useRef(false);

  const login = async (email, password, rememberMe = false) => {
    setLoading(true);
    setError(null);
    pendingRememberMe = rememberMe;

    try {
      const result = await authService.login(email, password);
      if (result.success && result.requiresOtp) {
        return {
          success: true,
          requiresOtp: true,
          email: result.email,
        };
      } else {
        setError(result.message);
        return {
          success: false,
          error: result.message,
        };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const verifyLoginOtp = async (email, otp) => {
    setLoading(true);
    setError(null);

    try {
      const result = await authService.verifyLoginOtp(email, otp);
      if (result.success) {
        saveAuthData(
          result.token,
          { email: result.email, role: result.role },
          pendingRememberMe,
        );

        setUser({ email: result.email, role: result.role });
        console.log("User logged in successfully");
        return {
          success: true,
          token: result.token,
        };
      } else {
        setError(result.message);
        return {
          success: false,
          error: result.message,
        };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const sendForgotPasswordOTP = async (email) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await authService.forgetPassword(email);
      if (result.success) {
        setSuccessMessage(result.message);
        console.log("FORGOT PASSWORD OTP SENT");
        return { success: true, email: result.email };
      } else {
        setError(result.message);
        return { success: false, error: result.message };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const verifyResetOtp = async (email, otp) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await authService.verifyResetOtp(email, otp);
      if (result.success) {
        setSuccessMessage(result.message);
        console.log("RESET OTP VERIFIED");
        return { success: true, message: result.message };
      } else {
        setError(result.message);
        return { success: false, error: result.message };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email, otp, newPassword) => {
    setLoading(true);
    setError(null);

    try {
      const result = await authService.resetPassword(email, otp, newPassword);
      if (result.success) {
        console.log("PASSWORD RESET SUCCESSFUL");
        return {
          success: true,
          message: result.message,
        };
      } else {
        setError(result.message);
        return {
          success: false,
          message: result.message,
        };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await authService.logout();

      if (result.success) {
        clearAuthData();

        setUser(null);

        console.log("Logged out successfully");
        return { success: true, message: result.message };
      } else {
        setError(result.message);
        return { success: false, error: result.message };
      }
    } catch (error) {
      console.error("Logout error:", error);
      clearAuthData();
      setUser(null);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (email, role) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await authService.createUser(email, role);
      if (result.success) {
        setSuccessMessage(result.message);
        return { success: true, email: result.email, message: result.message };
      } else {
        setError(result.message);
        return { success: false, error: result.message };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const getAllUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await authService.getAllUsers();
      if (result.success) {
        return { success: true, users: result.users };
      } else {
        setError(result.message);
        return { success: false, error: result.message };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const getAllUsersExceptMe = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await authService.getAllUsersExceptMe();
      if (result.success) {
        return { success: true, users: result.users };
      } else {
        setError(result.message);
        return { success: false, error: result.message };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const changeUserRole = async (targetUserEmail, newRole) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await authService.changeUserRole(targetUserEmail, newRole);
      if (result.success) {
        setSuccessMessage(result.message);
        return { success: true, message: result.message };
      } else {
        setError(result.message);
        return { success: false, error: result.message };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  return {
    login,
    verifyLoginOtp,
    sendForgotPasswordOTP,
    verifyResetOtp,
    resetPassword,
    logout,
    createUser,
    getAllUsers, 
    getAllUsersExceptMe, 
    changeUserRole,
    loading: loading || authLoading,
    error,
    successMessage,
    isAuthenticated: !!user,
    user,
    authChecked,
  };
};
